/**
 * Public self-claim pool API client (tokenized, no Cognito). Backs the
 * /tasks page — hits the public /tasks/* routes with the member's ?t token.
 * Mirrors RecipientPortal's hostname-derived API host (this is a public page).
 */
const API_HOST = (() => {
  if (typeof window === 'undefined') return 'https://api.badges.ninja';
  return window.location.hostname.includes('staging')
    ? 'https://api-staging.badges.ninja'
    : 'https://api.badges.ninja';
})();

export interface PoolTask {
  taskId: string;
  type: string;
  title: string;
  platform: string | null;
  slug: string | null;
  payload: string | null;
  status: string;
  claimedByName: string | null;
  claimedAt: number | null;
  publishedUrl: string | null;
  publishedAt: number | null;
  backlinkStatus: string | null;
  createdAt: number | null;
}

export interface PoolResponse {
  member: { id: string; name: string };
  available: PoolTask[];
  mine: PoolTask[];
}

async function parse(res: Response) {
  const data = await res.json().catch(() => ({ error: res.statusText }));
  if (!res.ok) throw new Error(data.detail || data.error || 'Request failed');
  return data;
}

export async function getPool(token: string): Promise<PoolResponse> {
  const res = await fetch(`${API_HOST}/tasks/pool?t=${encodeURIComponent(token)}`);
  return parse(res);
}

export async function claimTask(token: string, taskId: string): Promise<{ ok: boolean; task: PoolTask }> {
  const res = await fetch(`${API_HOST}/tasks/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, taskId }),
  });
  return parse(res);
}

export async function updateTask(
  token: string,
  taskId: string,
  status: 'in-progress' | 'published' | 'released',
  publishedUrl?: string,
): Promise<{ ok: boolean; task: PoolTask }> {
  const res = await fetch(`${API_HOST}/tasks/update`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ t: token, taskId, status, publishedUrl }),
  });
  return parse(res);
}
